import { useEffect, useState } from 'react';

import { zodResolver } from '@hookform/resolvers/zod';
import { ChevronDown, KeyRound, Plus, RotateCw } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import { FormField, FormInput, FormSelect } from '@/components/form';
import { Button } from '@/components/ui/button';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { useConfigurations } from '@/hooks/useConfigurations';
import { scrollToFirstError, mergeApiErrors } from '@/lib/form-utils';
import { useFeaturesStore } from '@/stores/features.store';

import { defaultUserFormValues } from './types';

import type { UserFormProps, UserFormValues } from './types';

const buildUserFormSchema = (isEditing: boolean) =>
  z
    .object({
      name: z.string().min(2, 'Name must be at least 2 characters'),
      email: z.string().email('Invalid email address'),
      role: z.enum(['admin', 'user', 'extension_only', 'client']),
      password: z.string(),
      confirmPassword: z.string(),
      configuration_ids: z.array(z.string()),
    })
    .superRefine(({ password, confirmPassword }, ctx) => {
      if (!isEditing && password.length < 8) {
        ctx.addIssue({
          path: ['password'],
          code: z.ZodIssueCode.custom,
          message: 'Password must be at least 8 characters',
        });
      }

      if (isEditing && password.length > 0 && password.length < 8) {
        ctx.addIssue({
          path: ['password'],
          code: z.ZodIssueCode.custom,
          message: 'Password must be at least 8 characters',
        });
      }

      if (password.length > 0 && password !== confirmPassword) {
        ctx.addIssue({
          path: ['confirmPassword'],
          code: z.ZodIssueCode.custom,
          message: 'Passwords do not match',
        });
      }
    });

export const UserForm = ({
  initialValues,
  isEditing = false,
  allowProfileEdit = true,
  onSubmit,
  submitLabel = 'Save',
  showReset = false,
  onReset,
  errors: apiErrors = [],
  isLoading = false,
}: UserFormProps) => {
  const [isPasswordOpen, setIsPasswordOpen] = useState(false);
  // Clients view: 'client' role users get scoped, read-only access to the
  // configurations picked below.
  const clientsViewEnabled = useFeaturesStore((s) => s.clientsView);
  const { data: configurations = [] } = useConfigurations();

  const {
    register,
    handleSubmit,
    reset,
    setError,
    watch,
    setValue,
    formState: { errors },
  } = useForm<UserFormValues>({
    resolver: zodResolver(buildUserFormSchema(isEditing)),
    defaultValues: { ...defaultUserFormValues, ...initialValues },
    mode: 'onBlur',
    reValidateMode: 'onChange',
  });

  const selectedRole = watch('role');
  const selectedConfigIds = watch('configuration_ids') ?? [];

  const toggleConfigId = (id: string, checked: boolean) => {
    const next = checked
      ? [...selectedConfigIds, id]
      : selectedConfigIds.filter((existing) => existing !== id);
    setValue('configuration_ids', next, { shouldDirty: true });
  };

  // Merge API errors when they change
  useEffect(() => {
    if (apiErrors.length > 0) {
      mergeApiErrors(apiErrors, setError);
    }
  }, [apiErrors, setError]);

  const hasPasswordErrors = Boolean(errors.password || errors.confirmPassword);

  const onFormSubmit = handleSubmit(
    (data) => {
      onSubmit(data);
    },
    (formErrors) => {
      scrollToFirstError(formErrors);
    },
  );

  const handleReset = () => {
    reset({ ...defaultUserFormValues, ...initialValues });
    onReset?.();
  };

  return (
    <form className="space-y-6" onSubmit={onFormSubmit}>
      <div className="grid gap-4 md:grid-cols-2">
        <FormField label="Full name" htmlFor="name" error={errors.name?.message} required>
          <FormInput
            id="name"
            placeholder="e.g. Olivia Hughes"
            autoComplete="off"
            error={!!errors.name}
            disabled={!allowProfileEdit}
            {...register('name')}
          />
        </FormField>

        <FormField label="Work email" htmlFor="email" error={errors.email?.message} required>
          <FormInput
            id="email"
            type="email"
            placeholder="user@example.com"
            autoComplete="off"
            error={!!errors.email}
            disabled={!allowProfileEdit}
            {...register('email')}
          />
        </FormField>
      </div>

      <FormField label="Role" htmlFor="role" error={errors.role?.message} required>
        <FormSelect
          id="role"
          error={!!errors.role}
          disabled={!allowProfileEdit}
          {...register('role')}
        >
          <option value="admin">Admin</option>
          <option value="user">User</option>
          {clientsViewEnabled && <option value="client">Client (read-only calls)</option>}
        </FormSelect>
      </FormField>

      {clientsViewEnabled && selectedRole === 'client' && (
        <FormField
          label="Allowed configurations"
          htmlFor="configuration_ids"
          error={errors.configuration_ids?.message}
        >
          <div className="border-input max-h-48 space-y-1 overflow-y-auto rounded-md border p-3">
            {configurations.length === 0 ? (
              <p className="text-muted-foreground text-sm">No configurations available.</p>
            ) : (
              configurations.map((config) => (
                <label
                  key={config.id}
                  className="hover:bg-muted/50 flex cursor-pointer items-center gap-2 rounded px-1 py-0.5 text-sm"
                >
                  <input
                    type="checkbox"
                    className="h-4 w-4"
                    checked={selectedConfigIds.includes(config.id)}
                    onChange={(e) => toggleConfigId(config.id, e.target.checked)}
                    disabled={!allowProfileEdit}
                  />
                  <span className="truncate">{config.name}</span>
                </label>
              ))
            )}
          </div>
          <p className="text-muted-foreground mt-1 text-xs">
            The user will only see calls belonging to the selected configurations.
          </p>
        </FormField>
      )}

      <Collapsible open={isPasswordOpen || hasPasswordErrors} onOpenChange={setIsPasswordOpen}>
        <CollapsibleTrigger asChild>
          <button
            type="button"
            className="border-input bg-background hover:bg-muted/50 flex w-full items-center justify-between rounded-md border px-4 py-2 text-left"
          >
            <div className="flex items-center gap-2">
              <KeyRound className="h-4 w-4" />
              <span className="text-sm font-medium">
                {isEditing ? 'Change password' : 'Set password'}
              </span>
            </div>
            <ChevronDown
              className={`h-4 w-4 transition-transform ${isPasswordOpen ? 'rotate-180' : ''}`}
            />
          </button>
        </CollapsibleTrigger>
        <CollapsibleContent className="mt-4">
          <div className="grid gap-4 md:grid-cols-2">
            <FormField
              label={isEditing ? 'New password' : 'Password'}
              htmlFor="password"
              error={errors.password?.message}
              required={!isEditing}
            >
              <FormInput
                id="password"
                type="password"
                placeholder={isEditing ? 'Leave blank to keep current password' : 'Enter password'}
                autoComplete="new-password"
                error={!!errors.password}
                {...register('password')}
              />
            </FormField>

            <FormField
              label={isEditing ? 'Confirm new password' : 'Confirm password'}
              htmlFor="confirmPassword"
              error={errors.confirmPassword?.message}
              required={!isEditing}
            >
              <FormInput
                id="confirmPassword"
                type="password"
                placeholder={isEditing ? 'Confirm new password' : 'Confirm password'}
                autoComplete="new-password"
                error={!!errors.confirmPassword}
                {...register('confirmPassword')}
              />
            </FormField>
          </div>
        </CollapsibleContent>
      </Collapsible>

      <div className="flex justify-end gap-3">
        {showReset && (
          <Button type="button" variant="outline" onClick={handleReset}>
            <RotateCw className="h-4 w-4" />
            Reset
          </Button>
        )}
        <Button type="submit" disabled={isLoading}>
          <Plus className="h-4 w-4" />
          {submitLabel}
        </Button>
      </div>
    </form>
  );
};
